import { useEffect, useState } from "react";
import useAxios from "@/hooks/useAxios";
import { config } from "@/config";
import { useSelector } from "react-redux";
import { ReduxState } from "@/redux/type";
import { formatNumber } from "@/utils/utilityFunctions";
import YearSelector from "@/pages/overview/YearSelector";
import LoadingSkeleton from "@/pages/overview/LoadingSkeleton";

interface CollectionVolume {
    Collection: string;
    TotalOrderVolume: string;
}

export default function CollectionVolumes() {
    const [volumes, setVolumes] = useState<CollectionVolume[]>([]);
    const [loading, setLoading] = useState(true);
    const customerId = useSelector((state: ReduxState) => state.customer.cardCode);
    const axios = useAxios();
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);

    // Generate array of years (current year and 2 years back)
    const years = Array.from({ length: 3 }, (_, i) => currentYear - i);

    useEffect(() => {
        async function getCollectionVolumes() {
            try {
                setLoading(true);
                const res = await axios.get(`${config.backendUrl}/order-volume-year-to-date`, {
                    params: { 
                        customerId,
                        year: selectedYear
                    }
                });
                setVolumes(res.data);
            } catch (error) {
                console.error("Error fetching collection volumes:", error);
            } finally {
                setLoading(false);
            }
        }
        getCollectionVolumes();
    }, [customerId, selectedYear]);

    if (loading) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
                    <h2 className="text-lg font-semibold mb-4 md:mb-0">Collection Order Volumes</h2>
                    <YearSelector
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        years={years}
                        disabled={loading}
                    />
                </div>
                <LoadingSkeleton showTitle={false} lines={4} className="bg-transparent shadow-none p-0" />
            </div>
        );
    }

    const totalVolume = volumes.reduce((sum, vol) => sum + parseFloat(vol.TotalOrderVolume), 0);

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
                <h2 className="text-lg font-semibold mb-4 md:mb-0">Collection Order Volumes</h2>
                <YearSelector
                    selectedYear={selectedYear}
                    onYearChange={setSelectedYear}
                    years={years}
                />
            </div>
            <div className="space-y-4">
                {volumes.map((volume) => {
                    const percentage = (parseFloat(volume.TotalOrderVolume) / totalVolume) * 100;
                    return (
                        <div key={volume.Collection} className="space-y-1">
                            <div className="flex justify-between text-sm">
                                <span className="font-medium text-gray-700">{volume.Collection}</span>
                                <span className="text-gray-600">${formatNumber(parseFloat(volume.TotalOrderVolume))}</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                                <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${percentage}%` }}
                                />
                            </div>
                            <div className="text-xs text-gray-500 text-right">
                                {percentage.toFixed(1)}% of total
                            </div>
                        </div>
                    );
                })}
                <div className="pt-4 mt-4 border-t border-gray-100">
                    <div className="flex justify-between font-semibold">
                        <span>Total Volume</span>
                        <span>${formatNumber(totalVolume)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
} 